/**
 * Design tokens for the public-facing marketing site.
 *
 * Transcribed from the "Motzu Labs Marketing Site + App Shell" handoff (the
 * dark "tech" theme). The marketing site is the one surface that does not share
 * `theme/appTheme.ts`: it ships a dark palette, a three-family type stack and a
 * much tighter radius scale than the signed-in app, and several of its values
 * (hairlines, the page grid, the float/glow keyframes) have no palette
 * equivalent.
 *
 * `theme/marketingTheme.ts` folds these into the MUI theme that `PublicLayout`
 * provides; prefer the palette where an equivalent exists and reach for these
 * raw values only for the things MUI has no slot for.
 */

export const marketingColor = {
  /** Page background. */
  ink: '#0A1418',
  /** Cards, panels, alternating sections. */
  surface: '#0F1E24',
  /** Selected list row, inline code. */
  surfaceSelected: '#152A31',
  /** Inactive dots, de-emphasised strokes. */
  surfaceStrong: '#1E3A42',
  /** Brand green: primary buttons, accents, active nav, code highlights. */
  accent: '#5FE28A',
  /** `a:hover`. */
  accentHover: '#8CF0AC',
  /** Pressed / deep accent, for contrast against the accent fill. */
  accentDeep: '#3FBF6B',
  /** Headings, body-on-dark. */
  text: '#E8F3EF',
  /** Body copy, descriptions. */
  textSecondary: '#7C9C93',
  /** Eyebrows, meta, footer copy. */
  textMuted: '#4F6B62',
  /** 400-level codes, rebate badge. */
  warning: '#E8B85F',
  /** 401/403/429/500, MAP violations. */
  error: '#E86A5F',
} as const;

export const marketingLine = {
  /** Section borders, card borders, grid gaps. */
  hairline: 'rgba(95,226,138,0.15)',
  /** Emphasised card borders, outline buttons. */
  hairlineStrong: 'rgba(95,226,138,0.3)',
  /** Hover / focus borders. */
  hairlineHover: 'rgba(95,226,138,0.5)',
  warning: 'rgba(232,184,95,0.4)',
  error: 'rgba(232,106,95,0.5)',
} as const;

export const marketingSurface = {
  /** Sticky header: translucent ink over the page grid. */
  header: 'rgba(10,20,24,0.9)',
  headerBlur: 'blur(6px)',
  /** Accent wash behind hero art and icon tiles. */
  accentTint: 'rgba(95,226,138,0.12)',
  accentTintStrong: 'rgba(95,226,138,0.2)',
  warningTint: 'rgba(232,184,95,0.12)',
  errorTint: 'rgba(232,106,95,0.12)',
} as const;

/** 64px hairline grid carried by the page background. */
export const marketingGrid =
  'repeating-linear-gradient(0deg, rgba(95,226,138,0.035) 0 1px, transparent 1px 64px), ' +
  'repeating-linear-gradient(90deg, rgba(95,226,138,0.035) 0 1px, transparent 1px 64px)';

export const marketingFont = {
  /** Headings, buttons. */
  display: '"Space Grotesk", "Inter", "Helvetica", "Arial", sans-serif',
  /** Body copy. */
  body: '"Manrope", "Inter", "Helvetica", "Arial", sans-serif',
  /** Eyebrows, nav, code, data, meta. */
  mono: '"JetBrains Mono", "IBM Plex Mono", "Menlo", "Consolas", monospace',
} as const;

export const marketingRadius = {
  /** Buttons, chips, eyebrows. */
  sm: 3,
  /** Cards, panels. */
  md: 6,
  lg: 8,
} as const;

export const marketingShadow = {
  panel: '0 30px 70px rgba(0,0,0,0.5)',
  card: '0 24px 60px rgba(0,0,0,0.45)',
  chip: '0 18px 40px rgba(0,0,0,0.45)',
} as const;

export const marketingLayout = {
  /** Sticky header height — scroll offsets and sticky panels key off this. */
  headerHeight: 76,
  /** Content max width; narrow variants are 800 / 1000 / 1160. */
  maxWidth: 1280,
  sectionPaddingY: { xs: 7, md: 12 },
  sectionPaddingX: { xs: 3, md: 6 },
} as const;

/**
 * Eyebrow label: mono, lowercase, `// prefixed`, hairline box.
 * Spread onto an `sx` prop.
 */
export const marketingEyebrow = {
  display: 'inline-block',
  fontFamily: marketingFont.mono,
  fontSize: 12,
  lineHeight: 1.4,
  color: marketingColor.accent,
  border: `1px solid ${marketingLine.hairlineStrong}`,
  borderRadius: `${marketingRadius.sm}px`,
  padding: '6px 12px',
  textTransform: 'lowercase',
} as const;

/** Keyframes used by the hero art and status dots. */
export const marketingKeyframes = {
  '@keyframes motzuFloatY': {
    '0%, 100%': { marginTop: 0 },
    '50%': { marginTop: '-10px' },
  },
  '@keyframes motzuPulseGlow': {
    '0%, 100%': { boxShadow: '0 0 24px rgba(95,226,138,0.12)' },
    '50%': { boxShadow: '0 0 44px rgba(95,226,138,0.28)' },
  },
  '@keyframes motzuBlink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
} as const;

/*
 * Shared section/CTA styling.
 *
 * The marketing pages previously filled hero and CTA bands with `primary.main`
 * and sat white buttons on top. The handoff inverts that: bands are ink or
 * raised surface, and the brand green is reserved for accents and the primary
 * CTA. These fragments keep that consistent across pages — spread them into
 * `sx` rather than restating the colours.
 */

/** Full-width hero / CTA band: ink ground with a centred accent glow. */
export const marketingBandSx = {
  position: 'relative',
  backgroundColor: marketingColor.ink,
  backgroundImage:
    'radial-gradient(circle at 50% 40%, rgba(95,226,138,0.14) 0%, transparent 65%)',
  color: marketingColor.text,
  borderTop: `1px solid ${marketingLine.hairline}`,
  borderBottom: `1px solid ${marketingLine.hairline}`,
} as const;

/** Alternating raised band (`#0F1E24`) between ink sections. */
export const marketingRaisedBandSx = {
  backgroundColor: marketingColor.surface,
  color: marketingColor.text,
  borderTop: `1px solid ${marketingLine.hairline}`,
  borderBottom: `1px solid ${marketingLine.hairline}`,
} as const;

/** Accent-filled tile, avatar or numbered step — ink glyph on brand green. */
export const marketingAccentTileSx = {
  backgroundColor: marketingColor.accent,
  color: marketingColor.ink,
} as const;

/** Primary CTA sitting on a band. */
export const marketingBandCtaSx = {
  backgroundColor: marketingColor.accent,
  color: marketingColor.ink,
  '&:hover': { backgroundColor: marketingColor.accentHover },
} as const;

/** Secondary CTA sitting on a band — hairline outline, accent label. */
export const marketingBandGhostCtaSx = {
  borderColor: marketingLine.hairlineStrong,
  color: marketingColor.accent,
  '&:hover': {
    borderColor: marketingColor.accent,
    backgroundColor: marketingSurface.accentTint,
  },
} as const;

/**
 * Light plate behind third-party partner marks.
 *
 * Platform logos are supplied in their own brand colours and several are dark
 * (BigCommerce, Lightspeed), so they disappear against the ink ground. Sitting
 * them on a light tile keeps every mark legible without recolouring anyone's
 * logo.
 */
export const marketingLogoPlateSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#FFFFFF',
  borderRadius: `${marketingRadius.md}px`,
  padding: '8px 10px',
  flexShrink: 0,
} as const;

/**
 * Plate behind manufacturer product photography.
 *
 * Catalog images come from the manufacturers shot on white, either as white
 * JPEGs or as transparent PNGs of near-black camera bodies. Both fail on the
 * ink ground — the first as a glaring white rectangle inside a dark card, the
 * second as a black camera that all but vanishes. The photos are the product's
 * own assets and must not be recoloured, so the surface under them stays white
 * and the card frame does the containing.
 */
export const marketingPhotoPlateSx = {
  backgroundColor: '#FFFFFF',
} as const;
